import { MoreOutlined, UserOutlined } from '@ant-design/icons';
import { nonNullable } from '@repo/utils';
import { type HandleProps, Position } from '@xyflow/react';
import {
  Button, Card, ConfigProvider, Drawer, Dropdown, Flex, type MenuProps,
  Tag,
  Typography,
} from 'antd';
import NodeHandler from 'automations/components/NodeHandler';
import { isDebugModeSelector } from 'automations/selectors/automationConfig';
import { canEditContentSelector } from 'automations/selectors/reactFlow';
import clsx from 'clsx';
import { type ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';

import ErrorRender from './ErrorRender';
import styles from './NodeUi.module.scss';

export interface NodeUiProps {
  nodeId: string;
  nodeTitle: string;
  nodeSubtitle: React.ReactNode;
  icon?: {
    iconContent: ReactNode;
    iconBgClassName?: string;
  };
  indicator?: {
    indicatorIcon: ReactNode;
    indicatorContent: string | ReactNode;
  };
  sourceHandle?: {
    id?: string;
    onClick?: HandleProps['onClick'];
  };
  targetHandle?: {
    id?: string;
    onClick?: HandleProps['onClick'];
  };
  contextMenu?: {
    onEdit?: () => void;
    onCopy?: () => void;
    onDelete?: () => void;
    onOpenDetails?: () => void;
  };
  additionalUiClassName?: string;
  AdditionalUI?: ReactNode;
  hasErrors: boolean;
  errorMessages: ReactNode[];
  debugContent?: any;
  successExecutionCounts?: number;
  backgroundStatus?: 'default' | 'success' | 'failed';
}

export default function NodeUi({
  nodeId,
  nodeTitle,
  nodeSubtitle,
  icon,
  indicator,
  sourceHandle,
  targetHandle,
  contextMenu,
  additionalUiClassName,
  AdditionalUI,
  hasErrors,
  errorMessages,
  debugContent,
  successExecutionCounts,
  backgroundStatus = 'default',
}: NodeUiProps) {
  const isDebugMode = useSelector(isDebugModeSelector) || null;
  const [showDebugDrawer, setShowDebugDrawer] = useState(false);
  const isIndicatorPresent = !!indicator && indicator.indicatorContent;
  const canEditContent = useSelector(canEditContentSelector(nodeId));

  const menuItems: MenuProps['items'] = [
    contextMenu?.onEdit && {
      label: 'Edit / View',
      key: 'edit',
      onClick: contextMenu.onEdit,
    },
    contextMenu?.onCopy && {
      label: 'Copy',
      key: 'copy',
      onClick: contextMenu.onCopy,
    },
    contextMenu?.onOpenDetails && {
      label: 'Open Details',
      key: 'open_details',
      onClick: contextMenu.onOpenDetails,
    },
    contextMenu?.onDelete && {
      label: 'Delete',
      key: 'delete',
      danger: true,
      onClick: contextMenu.onDelete,
    },
    isDebugMode && {
      label: 'View Content',
      key: 'view_content',
      onClick: () => setShowDebugDrawer(true),
    },
  ].filter(nonNullable);

  return (
    <>
      {(isDebugMode && debugContent) && (
        <Drawer
          title="Node Data"
          size="large"
          open={showDebugDrawer}
          destroyOnClose
          onClose={() => setShowDebugDrawer(false)}
        >
          <Card>
            <pre>{JSON.stringify(debugContent, null, 2)}</pre>
          </Card>
        </Drawer>
      )}
      <div className={styles.nodeUiContainer}>
        {Boolean(sourceHandle) && (
          <NodeHandler
            type="target"
            position={Position.Top}
            onClick={sourceHandle?.onClick}
            id={sourceHandle?.id}
            nodeId={nodeId}
          />
        )}
        <div className={styles.nodeContainer}>
          {isIndicatorPresent && (
            <Flex className={clsx(
              styles.indicator,
              backgroundStatus === 'success' && styles.successBackground,
              backgroundStatus === 'failed' && styles.failedBackground,
            )}
            >
              {indicator.indicatorIcon}
              <Typography.Text>{indicator.indicatorContent}</Typography.Text>
            </Flex>
          )}
          {Number.isInteger(successExecutionCounts) && (
            <Tag
              color="success"
              icon={<UserOutlined />}
              className={styles.successExecutionCounts}
            >
              {successExecutionCounts}
            </Tag>
          )}
          <Flex className={clsx(
            styles.container,
            !isIndicatorPresent && styles.containerWithoutIndicator,
            backgroundStatus === 'success' && styles.successBackground,
            backgroundStatus === 'failed' && styles.failedBackground,
          )}
          >
            <div className={styles.contentContainer}>
              {icon?.iconContent && (
                <div className={clsx(styles.iconContainer, icon?.iconBgClassName)}>
                  {icon.iconContent}
                </div>
              )}
              <Flex vertical className={styles.content}>
                <Typography.Text strong className={styles.title}>{nodeTitle}</Typography.Text>
                <Typography.Text
                  className={styles.subtitle}
                  type="secondary"
                >
                  {nodeSubtitle}
                </Typography.Text>
              </Flex>
            </div>
            <div className={styles.actions}>
              {menuItems.length > 0 && (
                <Dropdown
                  menu={{ items: menuItems }}
                  arrow
                  placement="bottomRight"
                  trigger={['click']}
                >
                  <Button icon={<MoreOutlined />} type="text" />
                </Dropdown>
              )}
            </div>
          </Flex>
        </div>
        {Boolean(targetHandle) && (
          <NodeHandler
            type="source"
            position={Position.Bottom}
            onClick={targetHandle?.onClick}
            id={targetHandle?.id}
            nodeId={nodeId}
          />
        )}
        {hasErrors && <ErrorRender errorMessages={errorMessages} />}
        <div className={clsx(styles.additionalUiContainer, additionalUiClassName)}>
          <ConfigProvider componentDisabled={!canEditContent}>
            {AdditionalUI}
          </ConfigProvider>
        </div>
      </div>
    </>
  );
}
